import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import FormModal from "@/components/FormModal/FormModal";
import formRenderer from "@/lib/formRenderer";
import type { FormConfig } from "@/lib/formRenderer";
import PlusIcon from "@/components/SVGComponents/PlusIcon";
import { combineClasses, notBlankValidator, requiredMessage } from "@/lib/utils";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { getRolePermissionsByRoleId } from "@/store/slices/roleSlice";
import type { Role } from "@/services/api/roles";
import styles from "./NewRoleModal.module.scss";

const ROLE_NAME_MAX_LENGTH = 50;
const DESCRIPTION_MAX_LENGTH = 255;

const ROLE_FIELDS: FormConfig = {
  roleName: {
    controlType: "input",
    label: "Role name",
    labelVariant: "caption",
    placeholder: "e.g. Ticketing Lead",
    validations: {
      required: requiredMessage("Role name"),
      validate: notBlankValidator("Role name"),
      maxLength: {
        value: ROLE_NAME_MAX_LENGTH,
        message: `Role name must not exceed ${ROLE_NAME_MAX_LENGTH} characters`,
      },
    },
    props: {
      maxLength: ROLE_NAME_MAX_LENGTH,
      className: combineClasses(styles.fieldInput),
    },
  },
  description: {
    controlType: "textarea",
    label: "Role description",
    labelVariant: "caption",
    placeholder: "What this role is for — shown next to its name in the permissions grid.",
    validations: {
      required: requiredMessage("Role description"),
      validate: notBlankValidator("Role description"),
      maxLength: {
        value: DESCRIPTION_MAX_LENGTH,
        message: `Description must not exceed ${DESCRIPTION_MAX_LENGTH} characters`,
      },
    },
    props: {
      maxLength: DESCRIPTION_MAX_LENGTH,
      className: combineClasses(styles.fieldTextarea),
    },
  },
};

export interface NewRoleFormValues {
  roleName: string;
  description: string;
}

export interface NewRoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  roleItem?: Role | null;
  onSubmit: (values: NewRoleFormValues & { permissionIds: number[] }) => void | Promise<void>;
}

const NewRoleModal = ({ isOpen, onClose, roleItem = null, onSubmit }: NewRoleModalProps) => {
  const isEditing = roleItem !== null;
  const dispatch = useAppDispatch();
  const { editingRolePermissions: modulePermissions, isFetchingEditingRolePermissions: isFetchingPermissions } =
    useAppSelector((state) => state.role);

  const [checkedPermissionIds, setCheckedPermissionIds] = useState<Set<number>>(new Set());

  const { register, handleSubmit, control, reset, formState } = useForm<NewRoleFormValues>({
    defaultValues: { roleName: "", description: "" },
  });

  useEffect(() => {
    if (!isOpen) return;
    reset({ roleName: roleItem?.name ?? "", description: roleItem?.description ?? "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, roleItem]);

  useEffect(() => {
    if (!isOpen || !roleItem) return;
    dispatch(getRolePermissionsByRoleId(roleItem.id));
  }, [isOpen, roleItem, dispatch]);

  useEffect(() => {
    if (!modulePermissions) {
      setCheckedPermissionIds(new Set());
      return;
    }
    const checked = new Set<number>();
    modulePermissions.forEach((module) => {
      module.permissions.forEach((permission) => {
        if (permission.hasPermission) checked.add(permission.permissionId);
      });
    });
    setCheckedPermissionIds(checked);
  }, [modulePermissions]);

  const allPermissions = useMemo(() => (modulePermissions ?? []).flatMap((module) => module.permissions), [modulePermissions]);

  const togglePermission = (permissionId: number) => {
    setCheckedPermissionIds((prev) => {
      const next = new Set(prev);
      if (next.has(permissionId)) {
        next.delete(permissionId);
      } else {
        next.add(permissionId);
      }
      return next;
    });
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFormSubmit = handleSubmit(async (values) => {
    await onSubmit({ ...values, permissionIds: Array.from(checkedPermissionIds) });
  });

  return (
    <FormModal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditing ? "Update role" : "New role"}
      subtitle={
        isEditing
          ? "Update this role's name, description, and permissions."
          : "A new role appears in the roles list below, with no access to any module by default."
      }
      onSubmit={handleFormSubmit}
      submitLabel={isEditing ? "Update" : "Create role"}
      submitIcon={<PlusIcon />}
      submitDisabled={formState.isSubmitting}
      className={combineClasses(styles.newRoleModal)}
    >
      {formRenderer<NewRoleFormValues>(ROLE_FIELDS, formState, register, control)}

      {isEditing && (
        <div className={styles.permissionsSection}>
          <span className={styles.permissionsLabel}>Permissions</span>
          {isFetchingPermissions ? (
            <p className={styles.permissionsMessage}>Loading permissions…</p>
          ) : allPermissions.length === 0 ? (
            <p className={styles.permissionsMessage}>No permissions available.</p>
          ) : (
            <div className={styles.permissionsGrid}>
              {allPermissions.map((permission) => (
                <label key={permission.permissionId} className={styles.permissionItem}>
                  <input
                    type="checkbox"
                    checked={checkedPermissionIds.has(permission.permissionId)}
                    onChange={() => togglePermission(permission.permissionId)}
                  />
                  {permission.permissionName}
                </label>
              ))}
            </div>
          )}
        </div>
      )}
    </FormModal>
  );
};

export default NewRoleModal;
